/**
 * CP-1: Tenant List
 * Table with tenant overview and actions
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { 
  Plus, Search, Building2, Wifi, Bell, AlertTriangle, 
  ExternalLink, RefreshCw, Loader2 
} from 'lucide-react';

export default function TenantListPage() {
  const [tenants, setTenants] = useState([]);
  const [tenantStats, setTenantStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTenant, setNewTenant] = useState({
    name: '', code: '', contact_name: '', contact_email: ''
  });
  const navigate = useNavigate();

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await controlApi.getTenants();
      setTenants(response.data);
      
      // Fetch stats for each tenant
      const statsPromises = response.data.map(t => 
        controlApi.getTenantStats(t.id).then(res => ({ id: t.id, ...res.data }))
      );
      const allStats = await Promise.all(statsPromises);
      const statsMap = {};
      allStats.forEach(s => { statsMap[s.id] = s; });
      setTenantStats(statsMap);
    } catch (error) {
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleCreateTenant = async () => {
    if (!newTenant.name || !newTenant.code) {
      toast.error('Name and code are required');
      return;
    }
    setCreating(true);
    try {
      await controlApi.createTenant(newTenant);
      toast.success('Tenant created successfully');
      setCreateDialogOpen(false);
      setNewTenant({ name: '', code: '', contact_name: '', contact_email: '' });
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setCreating(false);
    }
  };

  const filteredTenants = tenants.filter(t => 
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Tenants</h1>
          <p className="text-slate-400">Manage platform tenants and distributors</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={fetchTenants}
            className="border-slate-700 text-slate-300"
            data-testid="refresh-tenants"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-tenant-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Tenant
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-white">Create New Tenant</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Add a new customer tenant to the platform
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-300">Tenant Name</Label>
                  <Input
                    value={newTenant.name}
                    onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
                    placeholder="Apollo Pharmacy"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                    data-testid="tenant-name-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Tenant Code</Label>
                  <Input
                    value={newTenant.code}
                    onChange={(e) => setNewTenant({ ...newTenant, code: e.target.value.toUpperCase() })}
                    placeholder="APOLLO"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                    data-testid="tenant-code-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Contact Name</Label>
                  <Input
                    value={newTenant.contact_name}
                    onChange={(e) => setNewTenant({ ...newTenant, contact_name: e.target.value })}
                    placeholder="John Doe"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Contact Email</Label>
                  <Input
                    value={newTenant.contact_email}
                    onChange={(e) => setNewTenant({ ...newTenant, contact_email: e.target.value })}
                    placeholder="admin@apollo.com"
                    type="email"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
                <Button 
                  onClick={handleCreateTenant} 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={creating}
                  data-testid="submit-create-tenant"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Tenant
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search tenants..."
          className="pl-10 bg-slate-900 border-slate-700 text-white"
          data-testid="search-tenants"
        />
      </div>

      {/* Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-slate-800/50">
              <TableHead className="text-slate-400">Tenant</TableHead>
              <TableHead className="text-slate-400">Code</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400 text-center">
                <Wifi className="w-4 h-4 inline" /> Devices
              </TableHead>
              <TableHead className="text-slate-400 text-center">
                <AlertTriangle className="w-4 h-4 inline" /> Expiring
              </TableHead>
              <TableHead className="text-slate-400 text-center">
                <Bell className="w-4 h-4 inline" /> Open Alerts
              </TableHead>
              <TableHead className="text-slate-400">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
                </TableCell>
              </TableRow>
            ) : filteredTenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                  No tenants found
                </TableCell>
              </TableRow>
            ) : (
              filteredTenants.map((tenant) => {
                const stats = tenantStats[tenant.id] || {};
                return (
                  <TableRow 
                    key={tenant.id} 
                    className="border-slate-800 hover:bg-slate-800/50"
                    data-testid={`tenant-row-${tenant.code}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: tenant.primary_color || '#3b82f6' }}
                        >
                          <Building2 className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{tenant.name}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300 font-mono">{tenant.code}</TableCell>
                    <TableCell>
                      <Badge variant={tenant.status === 'active' ? 'default' : 'secondary'}>
                        {tenant.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center text-white">{stats.devices_count || 0}</TableCell>
                    <TableCell className="text-center">
                      <span className="text-amber-400">0</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-red-400">0</span>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/control/tenants/${tenant.id}`)}
                        className="text-blue-400 hover:text-blue-300"
                        data-testid={`open-tenant-${tenant.code}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
